import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ActivityIndicator, TouchableOpacity, Platform } from 'react-native';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import ShareCardTemplate from '../components/ShareCardTemplate';
import { getShareCard, ShareCard } from '../lib/shareCards';

type PublicShareRouteParams = {
  PublicShare: {
    code: string;
  };
};

export default function PublicShareScreen() {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        content: {
          flex: 1,
          alignItems: 'center',
          paddingHorizontal: theme.spacing.xl,
        },
        centered: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: theme.spacing.xl,
        },
        header: {
          paddingVertical: theme.spacing.xl,
        },
        headerTitle: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
        },
        cardContainer: {
          marginVertical: theme.spacing.xl,
          ...theme.shadows.cardShadow,
        },
        ctaContainer: {
          alignItems: 'center',
          marginTop: theme.spacing.xl,
        },
        ctaText: {
          ...theme.typography.lg,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginBottom: theme.spacing.lg,
        },
        footer: {
          marginTop: 'auto',
          paddingVertical: theme.spacing.xl,
        },
        footerText: {
          ...theme.typography.sm,
          color: theme.colors.textTertiary,
        },
        loadingText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.lg,
        },
        errorEmoji: {
          fontSize: 48,
          marginBottom: theme.spacing.lg,
        },
        errorTitle: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.sm,
        },
        errorMessage: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginBottom: theme.spacing.xl,
        },
      }),
    [theme],
  );

  const route = useRoute<RouteProp<PublicShareRouteParams, 'PublicShare'>>();
  const navigation = useNavigation();
  const [shareCard, setShareCard] = useState<ShareCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const code = route.params?.code;

  useEffect(() => {
    if (code && code !== 'undefined') {
      loadShareCard();
    } else {
      setError('Invalid share link');
      setLoading(false);
    }
  }, [code]);

  const loadShareCard = async () => {
    try {
      const card = await getShareCard(code);
      if (card) {
        setShareCard(card);
      } else {
        setError('Share card not found or has expired');
      }
    } catch (err) {
      setError('Failed to load share card');
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    if (Platform.OS === 'web') {
      window.location.href = '/welcome';
    } else {
      (navigation as any).navigate('SignUp');
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error || !shareCard) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centered}>
          <Text style={styles.errorEmoji}>🔗</Text>
          <Text style={styles.errorTitle}>Link Not Found</Text>
          <Text style={styles.errorMessage}>{error || 'This share link is invalid or has expired.'}</Text>
          <TouchableOpacity style={commonStyles.primaryButton} onPress={handleGetStarted}>
            <Text style={commonStyles.primaryButtonText}>Discover Cache</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Cache</Text>
        </View>

        <View style={styles.cardContainer}>
          <ShareCardTemplate
            totalValue={shareCard.total_value}
            itemCount={shareCard.item_count}
            topItems={shareCard.top_items}
            userName={shareCard.user_name || undefined}
          />
        </View>

        <View style={styles.ctaContainer}>
          <Text style={styles.ctaText}>
            Discover the hidden value in your home
          </Text>
          <TouchableOpacity style={commonStyles.primaryButton} onPress={handleGetStarted}>
            <Text style={commonStyles.primaryButtonText}>Get Started Free</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>
            {shareCard.view_count > 0 ? `${shareCard.view_count} views • ` : ''}
            Shared via Cache
          </Text>
        </View>
      </View>
    </SafeAreaView>
  );
}
